import type {
  CheckExtractResponseDto,
  CheckConfirmRequest,
  CheckImageUrlDto,
  MyCheckCaptureDto,
  ContributionDto,
  ReceiptExtractResponseDto,
  ReceiptConfirmRequest,
  ReceiptConfirmResponseDto,
  DashboardSummaryDto,
} from '@signalvote/shared';
import type { ApiClient } from '../client';
import { API_HEADERS } from '../headers';

/**
 * CAP-005 — capture calls hold a backend request open while Gemini reads the
 * check (2–4s typical). The shared wrapper has NO timeout today, so a dead
 * connection would hang the mobile UI indefinitely. We bound it client-side:
 * ~30s, then abort and let the treasurer re-tap (design § Timeouts — fail fast,
 * no retry chain; the backend HttpClient already caps its own Gemini call ~20s).
 */
export const CAPTURE_TIMEOUT_MS = 30_000;

/**
 * An authenticated image the caller loads itself rather than through this client — React Native's
 * `<Image source={{ uri, headers }} />` does its own fetch, natively. Shaped as RN's image source
 * without importing it, so the client stays platform-free.
 */
export interface AuthenticatedImageSource {
  uri: string;
  headers: Record<string, string>;
}

/**
 * Wrap a request in an AbortController that fires after CAPTURE_TIMEOUT_MS, and
 * compose it with an optional caller-supplied signal (TanStack Query's queryFn
 * signal). The timer is always cleared once the request settles so a completed
 * request is never aborted late.
 */
async function requestWithTimeout<T>(
  client: ApiClient,
  endpoint: string,
  options: RequestInit,
  callerSignal?: AbortSignal,
): Promise<T> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), CAPTURE_TIMEOUT_MS);

  if (callerSignal) {
    if (callerSignal.aborted) {
      controller.abort();
    } else {
      callerSignal.addEventListener('abort', () => controller.abort(), { once: true });
    }
  }

  try {
    return await client.request<T>(endpoint, { ...options, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}

export function createFinanceEndpoints(client: ApiClient) {
  return {
    /**
     * The Stripe Connect donation-portal reads, grouped as the API routes them
     * (`/api/finance/payments/*`). All Finance:View reads; nothing here writes —
     * onboarding, verification, slug and publish stay on the web, so the mobile
     * Donations screen never needs a mutation.
     */
    payments: {
      /**
       * Where to load the donation-page QR from — a PNG the server renders, so the phone carries
       * no QR library for the one screen that shows one (issue #656). Returned as a source rather
       * than bytes because React Native's `<Image>` fetches it natively; the headers are this
       * client's own, so the authenticated, campaign-scoped read is authenticated the same way
       * every other read is.
       *
       * The `campaign` query parameter is a CACHE KEY, not an argument — the server reads the
       * campaign from the header like every other endpoint and ignores this. Native image caches
       * key on the URI alone, so on one fixed path a treasurer who switches committees would be
       * served the previous committee's code out of cache and point a supporter at the wrong
       * donation page.
       *
       * The image 409s until the donation page is published — callers gate on the status read's
       * `published` instead of showing a broken image.
       */
      donationQrImageSource: (): AuthenticatedImageSource => {
        const headers = client.identityHeaders();
        const campaignId = headers[API_HEADERS.CAMPAIGN_ID];
        return {
          uri:
            `${client.getBaseURL()}/api/finance/payments/donation-qr` +
            (campaignId ? `?campaign=${encodeURIComponent(campaignId)}` : ''),
          headers,
        };
      },
    },

    /**
     * CAP-002 request 1 — multipart upload of the photographed check. The
     * caller builds the FormData (a `{ uri, name, type }` file part on mobile);
     * the wrapper passes FormData straight through with the correct headers.
     */
    extractCheck: (formData: FormData, signal?: AbortSignal) =>
      requestWithTimeout<CheckExtractResponseDto>(
        client,
        '/api/finance/captures/checks/extract',
        { method: 'POST', body: formData },
        signal,
      ),

    /**
     * CAP-003 request 2 — the idempotent commit; returns the Pending Contribution.
     * Routes through `requestWithTimeout` (not `client.post`) because the ~30s
     * capture signal must thread through — `client.post` has no signal parameter.
     */
    confirmCheck: (request: CheckConfirmRequest, signal?: AbortSignal) =>
      requestWithTimeout<ContributionDto>(
        client,
        '/api/finance/captures/checks/confirm',
        { method: 'POST', body: JSON.stringify(request) },
        signal,
      ),

    /**
     * Lists the still-Pending check captures the current user made, so a mobile
     * canvasser can find their own captures to fix before the treasurer confirms
     * them on the web. Finance.View; scoped to the caller + this campaign.
     */
    listMyCheckCaptures: () =>
      client.get<MyCheckCaptureDto[]>('/api/finance/captures/checks/mine'),

    /**
     * Short-lived presigned GET URL for a captured check image, so the edit
     * screen can show the photo the canvasser took. Finance.View.
     */
    getCheckImage: (contributionId: string) =>
      client.get<CheckImageUrlDto>(
        `/api/finance/captures/checks/${contributionId}/image`,
      ),

    /**
     * CAP-007 request 1 — multipart upload of the photographed receipt. Same
     * shape as `extractCheck`, reusing the identical ~30s capture timeout.
     */
    extractReceipt: (formData: FormData, signal?: AbortSignal) =>
      requestWithTimeout<ReceiptExtractResponseDto>(
        client,
        '/api/finance/captures/receipts/extract',
        { method: 'POST', body: formData },
        signal,
      ),

    /**
     * CAP-007 request 2 — the bank-first fork confirm. Routes through
     * `requestWithTimeout` (not `client.post`) so the ~30s capture signal threads
     * through, exactly as `confirmCheck` does.
     */
    confirmReceipt: (request: ReceiptConfirmRequest, signal?: AbortSignal) =>
      requestWithTimeout<ReceiptConfirmResponseDto>(
        client,
        '/api/finance/captures/receipts/confirm',
        { method: 'POST', body: JSON.stringify(request) },
        signal,
      ),
  };
}
